var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Action, { GetContext, } from "./Action";
export const MAXIMUM_RETRIES_ALLOWED = 10;
const MutlipleChoiceQuestion = Symbol('mutlipleChoice');
const TextQuestion = Symbol('text');
export const QuestionTypeMap = {
    [MutlipleChoiceQuestion]: 'multipleChoice',
    [TextQuestion]: 'text',
};
;
const defaultOptions = {
    choices: [],
    continueOnFailure: false,
    failedToAnswerResponse: 'Sorry, I do not understand your answer.',
    invalidAnswerResponse: 'Hmm. I didn\'t quite understand your answer. Please try again.',
    maxRetries: 1,
    type: TextQuestion,
    validateAnswer: () => true,
};
const QuestionAnswer = Symbol('answer');
const QuestionAnswerValidator = Symbol('answerValidator');
const QuestionBody = Symbol('body');
const QuestionChoices = Symbol('choices');
const QuestionFailedToAnswerRes = Symbol('failedToAnswerResponse');
const QuestionInvalidAnswerRes = Symbol('invalidAnswerResponse');
const QuestionIsAnswered = Symbol('isAnswered');
const QuestionIsFailed = Symbol('isFailed');
const QuestionMaxRetries = Symbol('maxRetries');
const QuestionShouldSendInvalidRes = Symbol('shouldSendInvalidResponse');
const QuestionType = Symbol('type');
export const QuestionEvaluate = Symbol('evaluate');
export const QuestionSetIsAnswered = Symbol('setIsAnswered');
export const QuestionSetIsFailed = Symbol('setIsFailed');
export const QuestionShouldContinueOnFail = Symbol('shouldContinueOnFailure');
export const QuestionSetShouldSendInvalidRes = Symbol('setShouldSendInvalidRes');
export default class Question extends Action {
    constructor(body, { choices = defaultOptions.choices, continueOnFailure = defaultOptions.continueOnFailure, failedToAnswerResponse = defaultOptions.failedToAnswerResponse, invalidAnswerResponse = defaultOptions.invalidAnswerResponse, maxRetries = defaultOptions.maxRetries, type = defaultOptions.type, validateAnswer = defaultOptions.validateAnswer, } = defaultOptions) {
        if (!Question.validString(body)) {
            throw new TypeError('The first argument of the Question constructor must be a non-empty string');
        }
        if ((type === TextQuestion)
            && (choices !== defaultOptions.choices)) {
            throw new TypeError('A text Question cannot have a \'choices\' option');
        }
        if ((type === MutlipleChoiceQuestion) && ((!choices)
            || (!Array.isArray(choices))
            || (choices.length < 2)
            || (choices.some((v) => typeof v !== 'function')))) {
            throw new TypeError('Multiple choice Questions must include a \'choices\' option, an array of at least 2 functions of a string which return a boolen');
        }
        if ((type === MutlipleChoiceQuestion)
            && (validateAnswer !== defaultOptions.validateAnswer)) {
            throw new TypeError('Multiple choice Questions cannot have a validateAnswer option');
        }
        if (!Question.validString(failedToAnswerResponse)) {
            throw new TypeError('Question failedToAnswerResponse option must be a non-empty string');
        }
        if (!Question.validString(invalidAnswerResponse)) {
            throw new TypeError('Question invalidAnswerResponse option must be a non-empty string');
        }
        if (isNaN(maxRetries) ||
            (f => (f(maxRetries) < 0
                || f(maxRetries) > MAXIMUM_RETRIES_ALLOWED))(Math.round)) {
            throw new TypeError(`Question maxRetries option must be a number from 0 to ${MAXIMUM_RETRIES_ALLOWED}.`);
        }
        if (type !== TextQuestion && type !== MutlipleChoiceQuestion) {
            type = TextQuestion;
        }
        if (typeof validateAnswer !== 'function') {
            throw new TypeError('Question validateAnswer option must be a function');
        }
        super();
        this[QuestionAnswer] = null;
        this[QuestionAnswerValidator] = validateAnswer;
        this[QuestionBody] = body;
        this[QuestionChoices] = choices;
        this[QuestionFailedToAnswerRes] = failedToAnswerResponse;
        this[QuestionInvalidAnswerRes] = invalidAnswerResponse;
        this[QuestionIsAnswered] = false;
        this[QuestionMaxRetries] = Number(maxRetries);
        this[QuestionIsFailed] = false;
        this[QuestionShouldContinueOnFail] = Boolean(continueOnFailure);
        this[QuestionShouldSendInvalidRes] = false;
        this[QuestionType] = type;
        this[GetContext] = this.getQuestionContext.bind(this);
    }
    static validString(s) {
        return (typeof s === 'string')
            && Boolean(s.length);
    }
    getQuestionContext() {
        return {
            answer: this.answer,
            body: this.body,
            questionType: QuestionTypeMap[this.type],
            wasAnswered: this.isAnswered,
            wasFailed: this.isFailed,
        };
    }
    handleInvalidAnswer(state) {
        if (state.question.attempts.length >= this.maxRetries) {
            this[QuestionIsFailed] = true;
        }
        else {
            this[QuestionShouldSendInvalidRes] = true;
        }
    }
    setAnswer(answer) {
        this[QuestionIsAnswered] = true;
        this[QuestionAnswer] = answer;
    }
    get answer() {
        return this[QuestionAnswer];
    }
    get body() {
        return this[QuestionBody];
    }
    get choices() {
        return this[QuestionChoices];
    }
    get failedAnswerResponse() {
        return this[QuestionFailedToAnswerRes];
    }
    get invalidAnswerResponse() {
        return this[QuestionInvalidAnswerRes];
    }
    get isAnswered() {
        return this[QuestionIsAnswered];
    }
    get isComplete() {
        return this.isAnswered || this.isFailed;
    }
    get isFailed() {
        return this[QuestionIsFailed];
    }
    get maxRetries() {
        return this[QuestionMaxRetries];
    }
    get type() {
        return this[QuestionType];
    }
    get shouldSendInvalidRes() {
        return this[QuestionShouldSendInvalidRes];
    }
    get validateAnswer() {
        return this[QuestionAnswerValidator];
    }
    [QuestionEvaluate](req, state) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!state.question.isAnswering)
                return;
            if ((this.type === Question.Types.Text)
                && (yield this.validateAnswer(req.body.Body))) {
                this.setAnswer(req.body.Body);
                return;
            }
            if (this.type === Question.Types.MultipleChoice) {
                const choices = yield Promise.all(this.choices.map((validate) => validate(req.body.Body)));
                const validChoices = choices.map((_, i) => i)
                    .filter(i => choices[i]);
                if (validChoices.length === 1) {
                    this.setAnswer(validChoices[0]);
                    return;
                }
            }
            this.handleInvalidAnswer(state);
        });
    }
    [QuestionSetIsAnswered]() {
        this[QuestionIsAnswered] = true;
    }
    [QuestionSetIsFailed]() {
        this[QuestionIsFailed] = true;
    }
    [QuestionSetShouldSendInvalidRes]() {
        this[QuestionShouldSendInvalidRes] = true;
    }
}
Question.Types = {
    MultipleChoice: MutlipleChoiceQuestion,
    Text: TextQuestion,
};
